import * as React from 'react'

import { create } from '@bufbuild/protobuf'
import { createConnectQueryKey } from '@connectrpc/connect-query'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { Flex, Grid, GridItem, Stack, Text } from '@chakra-ui/react'

import {
  Link,
  PrimaryButton,
  SecondaryButton,
  toaster,
} from '@enechain/ecloud-designsystem'
import { FaArrowUpRightFromSquareRegular } from '@enechain/ecloud-icons'

import { ProfilePicture } from '~/components/parts/ProfilePicture'
import { BffCommonService } from '~/gen/proto/bff/v1/common_pb'
import { LicenseSchema, Member } from '~/gen/proto/bff/v1/model_pb'
import i18n from '~/i18n'
import { NEW_MEMBER_KEY } from '~/pages/Members/create'
import { dict } from '~/pages/Members/dict'
import { useMemberMutation } from '~/pages/Members/hooks/useMemberMutation'
import { useSelectableUserRoles } from '~/pages/Members/hooks/useSelectableUserRoles'
import { useMemberMutationState } from '~/pages/Members/state/memberMutation'
import { isUserRoleConfigurable } from '~/pages/Members/utils/license'

type Props = {
  member: Member
  observed: Readonly<Member>
  onBack: () => void
}

const MemberConfirmView: React.FC<Props> = ({ member, observed, onBack }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isCreate = member.id === NEW_MEMBER_KEY
  const { discard: discardMember } = useMemberMutationState(member.id)
  const onSuccess = (): void => {
    const successMessage = isCreate
      ? t('pages.members.action.inviteMember.success')
      : t('message.success_change', {
          entity: t('domain.member'),
        })

    toaster.create({
      title: successMessage,
      type: 'success',
    })
    setTimeout(() => {
      void navigate({
        to: '/members',
      })
      discardMember()
    }, 5000)
  }
  const { mutate, isPending: isSavingMember } = useMemberMutation(
    onSuccess,
    member.id,
  )
  const queryClient = useQueryClient()
  const handleSubmit = (): void => {
    mutate({
      ...member,
      id: isCreate ? undefined : member.id,
      observed: observed,
    })
    void queryClient.invalidateQueries({
      queryKey: createConnectQueryKey({
        schema: BffCommonService.method.listMembers,
        cardinality: 'finite',
      }),
    })
  }

  const selectableUserRoles = useSelectableUserRoles()

  return (
    <Stack gap="6" width="100%">
      {!isCreate && (
        <Flex alignItems="center" gap="6" width="100%">
          {member.imageUrl !== '' && member.imageUrl !== undefined && (
            <ProfilePicture src={member.imageUrl} />
          )}
          <Flex flexDirection="column">
            <Text textStyle="headerMd">{member.name}</Text>
            <Text>{member.email}</Text>
          </Flex>
        </Flex>
      )}

      <Text textStyle="headerMd">{t('pages.members.info')}</Text>

      <Grid
        gridGap="4"
        gridTemplateColumns="320px 1fr"
        gridTemplateAreas={`
          'name vname'
          'englishName venglishName'
          'email vemail'
          'role vrole'
          'language vlanguage'
          'phoneNumber vphoneNumber'
          'mobileNumber vmobileNumber'`}
        width="full"
        marginBottom="2"
        padding="4"
        borderWidth="1px"
        borderColor="Border.lowEmphasis"
        borderRadius="radiiMd"
      >
        <GridItem gridArea="name" fontWeight="bold">
          {t('pages.members.label.name')}
        </GridItem>
        <GridItem gridArea="vname">{member.name}</GridItem>

        <GridItem gridArea="englishName" fontWeight="bold">
          {t('pages.members.label.englishName')}
        </GridItem>
        <GridItem gridArea="venglishName">{member.englishName}</GridItem>

        <GridItem gridArea="email" fontWeight="bold">
          {t('pages.members.label.email')}
        </GridItem>
        <GridItem gridArea="vemail">{member.email}</GridItem>

        <GridItem gridArea="role" fontWeight="bold">
          {t('pages.members.label.role')}
        </GridItem>
        <GridItem gridArea="vrole">{dict.role(member.role)}</GridItem>

        <GridItem gridArea="language" fontWeight="bold">
          {t('pages.members.label.language')}
        </GridItem>
        <GridItem gridArea="vlanguage">
          {dict.language(member.language)}
        </GridItem>

        <GridItem gridArea="phoneNumber" fontWeight="bold">
          {t('pages.members.label.phoneNumber')}
        </GridItem>
        <GridItem gridArea="vphoneNumber">{member.phoneNumber}</GridItem>

        <GridItem gridArea="mobileNumber" fontWeight="bold">
          {t('pages.members.label.mobileNumber')}
        </GridItem>
        <GridItem gridArea="vmobileNumber">{member.mobileNumber}</GridItem>
      </Grid>

      <Flex
        flexDirection="column"
        gap="4"
        display={
          isUserRoleConfigurable(
            selectableUserRoles.map((v) => v.license ?? create(LicenseSchema)),
          )
            ? 'flex'
            : 'none'
        }
      >
        <Text textStyle="headerMd">{t('pages.members.roleSettings')}</Text>
        <Grid
          gridGap="4"
          gridTemplateColumns="320px 1fr"
          width="full"
          padding="4"
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="radiiMd"
        >
          {selectableUserRoles.map((role) => {
            const { userRoleSetting, license } = role
            if (!license) {
              return null
            }
            if (
              license.isUserRoleConfigurable === false ||
              license.isProductPublic === false
            ) {
              return null
            }
            return (
              <React.Fragment key={license.planId}>
                <GridItem>
                  <Text fontWeight="bold">{`${license.productName} / ${i18n.language === 'en' ? license.engPlanName : license.planName}`}</Text>
                  <Link
                    href={`/user_roles/${license.planId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Flex alignItems="center" gap="1">
                      {t('pages.members.link.user_role_page')}
                      <FaArrowUpRightFromSquareRegular />
                    </Flex>
                  </Link>
                </GridItem>
                <GridItem>
                  {userRoleSetting
                    .filter((role) =>
                      member.userRoleCodes.some(
                        (code) => code === role.userRoleCode,
                      ),
                    )
                    .map((r) => {
                      return i18n.language === 'en'
                        ? r.userRoleEngName
                        : r.userRoleName
                    })
                    .join(', ')}
                </GridItem>
              </React.Fragment>
            )
          })}
        </Grid>
      </Flex>
      <Flex justifyContent="right" gap="2" width="100%">
        <SecondaryButton disabled={isSavingMember} onClick={onBack}>
          {t('action.back')}
        </SecondaryButton>
        <PrimaryButton loading={isSavingMember} onClick={handleSubmit}>
          {isCreate
            ? t('pages.members.action.inviteMember.confirm')
            : t('action.save')}
        </PrimaryButton>
      </Flex>
    </Stack>
  )
}

export default React.memo(MemberConfirmView)
